import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Context,
  type ReactNode,
} from 'react';
import type { JourneyLearningStatus } from '@wapharma/domain';
import { unwrapData } from '@wapharma/shared';
import { apiGet, apiPut } from '../api';
import { getToken } from '../auth';

export type JourneyProgressRow = {
  flowId: string;
  revision: number;
  status: JourneyLearningStatus;
  lastStepId: string | null;
};

export type JourneyProgressValue = {
  rows: JourneyProgressRow[];
  loading: boolean;
  refresh: () => Promise<JourneyProgressRow[]>;
  upsert: (
    flowId: string,
    patch: { status?: JourneyLearningStatus; lastStepId?: string | null },
  ) => Promise<JourneyProgressRow | null>;
  learningStatus: (flowId: string, revision: number) => JourneyLearningStatus | null;
};

/** Survive Vite HMR — shared store for Tour + Guide (one PUT must update checklist). */
const JourneyProgressContext: Context<JourneyProgressValue | null> =
  (import.meta.hot?.data.journeyProgressCtx as Context<JourneyProgressValue | null> | undefined) ??
  createContext<JourneyProgressValue | null>(null);
if (import.meta.hot) import.meta.hot.data.journeyProgressCtx = JourneyProgressContext;

export function JourneyProgressProvider({ children }: { children: ReactNode }) {
  const [rows, setRows] = useState<JourneyProgressRow[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) return [];
    setLoading(true);
    try {
      const raw = await apiGet<{ data: JourneyProgressRow[] } | JourneyProgressRow[]>(
        '/auth/journey-progress',
        token,
      );
      const data = unwrapData(raw) as JourneyProgressRow[];
      /* Never downgrade local COMPLETED — Terminer + readiness refresh race. */
      setRows((prev) => {
        const byKey = new Map<string, JourneyProgressRow>();
        for (const r of data) {
          byKey.set(`${r.flowId}:${r.revision}`, r);
        }
        for (const local of prev) {
          if (local.status !== 'COMPLETED') continue;
          const key = `${local.flowId}:${local.revision}`;
          const server = byKey.get(key);
          if (!server || server.status !== 'COMPLETED') {
            byKey.set(key, local);
          }
        }
        return [...byKey.values()];
      });
      return data;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const upsert = useCallback(
    async (flowId: string, patch: { status?: JourneyLearningStatus; lastStepId?: string | null }) => {
      const token = getToken();
      if (!token) return null;

      /* Optimistic — checklist updates before PUT / openDock refresh. */
      setRows((prev) => {
        const existing = prev.find((r) => r.flowId === flowId);
        const revision = existing?.revision ?? 1;
        const optimistic: JourneyProgressRow = {
          flowId,
          revision,
          status: patch.status ?? existing?.status ?? 'STARTED',
          lastStepId:
            patch.lastStepId !== undefined ? patch.lastStepId : (existing?.lastStepId ?? null),
        };
        const rest = prev.filter((r) => !(r.flowId === flowId && r.revision === revision));
        return [...rest, optimistic];
      });

      const raw = await apiPut<{ data: JourneyProgressRow } | JourneyProgressRow>(
        `/auth/journey-progress/${encodeURIComponent(flowId)}`,
        patch,
        token,
      );
      const row = unwrapData(raw) as JourneyProgressRow;
      setRows((prev) => {
        const rest = prev.filter((r) => !(r.flowId === flowId && r.revision === row.revision));
        return [...rest, row];
      });
      return row;
    },
    [],
  );

  const learningStatus = useCallback(
    (flowId: string, revision: number): JourneyLearningStatus | null => {
      const row = rows.find((r) => r.flowId === flowId && r.revision === revision);
      return row?.status ?? null;
    },
    [rows],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ rows, loading, refresh, upsert, learningStatus }),
    [rows, loading, refresh, upsert, learningStatus],
  );

  return (
    <JourneyProgressContext.Provider value={value}>{children}</JourneyProgressContext.Provider>
  );
}

export function useJourneyProgress(): JourneyProgressValue {
  const ctx = useContext(JourneyProgressContext);
  if (!ctx) {
    throw new Error('useJourneyProgress must be used within JourneyProgressProvider');
  }
  return ctx;
}
